import { useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"

import type { ApiEstimate } from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import { ByPart } from "./by-part"
import { ByPhoto } from "./by-photo"
import { useCanvas } from "./use-canvas"

// Two ways to edit the same estimate, one at a time:
//   By photo — a photo and the parts on it (photo → parts).
//   By part  — the parts and the photos that show them (part → photos).
// The total and Save are shared; everything else belongs to one view.

const usd = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)

type View = "photo" | "part"

export function EvidenceCanvas({
  estimate,
  onDirtyChange,
}: {
  estimate: ApiEstimate
  onDirtyChange?: (dirty: boolean) => void
}) {
  const c = useCanvas(estimate)
  const { dispatch } = c
  const images = (estimate.images ?? []).filter((img) => img.isActive !== false)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const view: View = params.get("view") === "part" || images.length === 0 ? "part" : "photo"
  const setView = (v: View) => {
    const next = new URLSearchParams(params)
    next.set("view", v)
    setParams(next, { replace: true })
  }

  useEffect(() => { onDirtyChange?.(c.dirty) }, [c.dirty, onDirtyChange])
  useEffect(() => { if (c.notice) toast(c.notice.text) }, [c.notice])

  const onSave = async () => {
    try {
      await c.save()
      toast.success("Saved")
      return true
    } catch {
      toast.error("Couldn't save — try again")
      return false
    }
  }

  // Done: save if there is anything to save, then show the estimate.
  const onDone = async () => {
    if (c.dirty && !(await onSave())) return
    navigate(`/estimates/${estimate.id}`)
  }

  const hours = c.lines.reduce((s, l) => s + (l.manHours ?? 0), 0)

  return (
    <div className="mx-auto max-w-[1280px]">
      <div className="sticky top-0 z-20 -mx-4 mb-5 flex flex-wrap items-center justify-between gap-3 bg-white/90 px-4 py-3 backdrop-blur-sm md:-mx-6 md:px-6">
        <div role="tablist" aria-label="Edit by" className="inline-flex gap-1.5">
          <ViewPill active={view === "photo"} onClick={() => setView("photo")} disabled={images.length === 0}>
            By photo
          </ViewPill>
          <ViewPill active={view === "part"} onClick={() => setView("part")}>
            By part
          </ViewPill>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-[13.5px] text-zinc-600">
            <span className="text-[16px] font-semibold tabular-nums text-zinc-900">
              {usd(estimate.min)} – {usd(estimate.max)}
            </span>
            <span className="ml-2">
              {c.lines.length} {c.lines.length === 1 ? "part" : "parts"} · {hours.toFixed(1)} h
            </span>
          </p>
          <button
            type="button"
            onClick={onSave}
            disabled={!c.dirty || c.saving}
            className={cx(
              "h-10 shrink-0 rounded-lg px-4 text-[13.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/40 focus-visible:ring-offset-2 disabled:cursor-default",
              c.dirty ? "bg-zinc-900 text-white hover:bg-zinc-800" : "border border-zinc-200 text-zinc-400",
            )}
          >
            {c.saving ? "Saving…" : c.dirty ? "Save" : "Saved"}
          </button>
          <button
            type="button"
            onClick={onDone}
            disabled={c.saving}
            className={cx(
              "h-10 shrink-0 rounded-lg px-4 text-[13.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/40 focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-50",
              c.dirty ? "border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50" : "bg-zinc-900 text-white hover:bg-zinc-800",
            )}
          >
            Done
          </button>
        </div>
      </div>

      {view === "photo" ? (
        <ByPhoto images={images} lines={c.lines} dispatch={dispatch} />
      ) : (
        <ByPart images={images} lines={c.lines} dispatch={dispatch} />
      )}
    </div>
  )
}

function ViewPill({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "h-10 rounded-full border px-4 text-[13.5px] font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30 disabled:opacity-40",
        active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50",
      )}
    >
      {children}
    </button>
  )
}
